import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  ArrowRight,
  Shield,
  FileScan,
  Brain,
  Users,
  Briefcase,
  Target,
  Check,
  Code,
  Layers
} from 'lucide-react';
import ResultsClient from '../data/api/ResultsClient';
import Loader from '../../../shared/components/Loader';
import RadarScoreChart from '../../../shared/components/charts/RadarScoreChart';
import ScoreBarChart from '../../../shared/components/charts/ScoreBarChart';
import TechBarChart from '../../../shared/components/charts/TechBarChart';
import Modal from '../../../shared/components/Modal';
import MatrixDonutGroup from '../../../shared/components/charts/MatrixDonutGroup';
import '../styles/ResultStatsPage.css';

const AnimatedScore: React.FC<{ value: number }> = ({ value }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1000;
    const increment = value / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return <>{displayValue}</>;
};

export interface ResultStatsPageProps {
  inlineSessionId?: string;
  inlineCandidateId?: string;
  inlineReturnPath?: string;
  isInline?: boolean;
  onDetailedClick?: () => void;
  onClose?: () => void;
}

const ResultStatsPage: React.FC<ResultStatsPageProps> = ({ inlineSessionId, inlineCandidateId, inlineReturnPath, isInline, onDetailedClick, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const scheduleId = location.state?.scheduleId;
  const sessionId = inlineSessionId || location.state?.sessionId;
  const candidateId = inlineCandidateId || location.state?.candidateId;
  const returnTab = location.state?.returnTab;
  const returnPath = inlineReturnPath || location.state?.returnPath;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [proctorData, setProctorData] = useState<any>(null);
  const [isTechModalOpen, setIsTechModalOpen] = useState(false);

  const handleBack = () => {
    if (isInline && onClose) {
      onClose();
      return;
    }
    if (returnPath) {
      navigate(returnPath);
    } else if (returnTab === 'applicant') {
      navigate('/dashboard/results', { state: { activeTab: 'applicant', applicantId: candidateId } });
    } else {
      navigate('/dashboard/results/learners', { state: { scheduleId } });
    }
  };

  const handleViewDetailed = () => {
    if (onDetailedClick) {
      onDetailedClick();
      return;
    }
    navigate('/dashboard/results/feedback', {
      state: { scheduleId, sessionId, candidateId, returnTab, returnPath }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDownloadReport = async () => {
    if (!sessionId) return;
    try {
      const response = await ResultsClient.getReport(sessionId, candidateId, false);
      if (response && response.report_url) {
        window.open(response.report_url, '_blank');
      } else {
        alert("Failed to generate report URL");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      alert("An error occurred while generating your report.");
    }
  };

  useEffect(() => {
    // Ensure the page starts at the top when navigating here
    window.scrollTo(0, 0);

    if (!sessionId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const [feedbackResponse, proctorResponse] = await Promise.all([
          ResultsClient.getFeedback(sessionId, candidateId).catch(() => null),
          ResultsClient.getProctoringData(sessionId, candidateId).catch(() => null)
        ]);
        
        setData(feedbackResponse);
        setProctorData(proctorResponse);
      } catch (error) {
        console.error("Error fetching stats data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionId, candidateId]);

  const coreCompReport = data?.feedback?.core_competencies_report;
  const coreCompetenciesArray = Array.isArray(coreCompReport?.competency_evaluations)
    ? coreCompReport.competency_evaluations
    : Array.isArray(coreCompReport)
    ? coreCompReport
    : [];

  const radarData = useMemo(
    () => coreCompetenciesArray.map((c: any) => ({
      subject: c.competency,
      A: c.score,
      fullMark: 10
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coreCompReport]
  );

  if (loading) {
    return <Loader isLoading={true} message="Assembling performance statistics..." />;
  }

  if (!data || !data.feedback) {
    return (
      <div className="result-stats-container" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <h2 style={{ color: '#0f172a' }}>Stats not available</h2>
        <p style={{ color: '#64748b' }}>We couldn't retrieve the statistics for this session.</p>
        <button className="rs-btn-back" onClick={handleBack} style={{ background: '#e2e8f0' }}>Back to Interviews</button>
      </div>
    );
  }

  const { feedback } = data;
  const sessionFeedback = feedback.session_feedback || {};
  const isCodingFeedback = feedback.round_type === 'CODING' || feedback.coding_evaluation != null;
  const overallScore = isCodingFeedback ? (feedback.technical_readiness_score * 10) : (sessionFeedback.technical_readiness_score * 10);
  const verdict = feedback.final_verdict || sessionFeedback.final_verdict || 'PENDING';
  const roundName = feedback.round_type?.replace(/_/g, ' ') || "Interview Performance";

  let verdictClass = 'pending';
  if (verdict.toUpperCase().includes('HIRE') && !verdict.toUpperCase().includes('NO HIRE')) verdictClass = 'success';
  else if (verdict.toUpperCase().includes('NO HIRE')) verdictClass = 'fail';
  else if (verdict !== 'PENDING') verdictClass = 'warn';

  const firstName = feedback.first_name || '';
  const lastName = feedback.last_name || '';
  const candidateName = [firstName, lastName].filter(Boolean).join(' ') || feedback.candidate_name || 'Candidate';


  const getSegments = (score: number) => {
    if (score >= 75) return 4;
    if (score >= 50) return 3;
    if (score >= 25) return 2;
    return 1;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderSegmentBars = (score: number) => {
    const activeSegments = getSegments(score);
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '10px' }}>{score}</span>
        <div className="rs-segment-bars">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className={`rs-segment-dash ${i < activeSegments ? 'rs-segment-active' : ''}`} />
          ))}
        </div>
      </div>
    );
  };

  const coreCompetencyNames = new Set(
    coreCompetenciesArray.map((c: any) => (c.competency || '').toLowerCase().trim())
  );
  
  const techScorecardItems = Array.isArray(feedback?.technical_skills_evaluation?.technical_scorecard) 
    ? feedback.technical_skills_evaluation.technical_scorecard 
    : [];
    
  const filteredTechSkills = techScorecardItems.filter(
    (s: any) => !coreCompetencyNames.has((s.skill || '').toLowerCase().trim())
  );

  const motionProps = {
    initial: { opacity: 0, y: 15 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 }
  };

  return (
    <div className={`result-stats-container ${isInline ? 'inline-stats-view' : ''}`}>
      {/* Top Navigation */}
      {!isInline ? (
        <div className="rs-header-nav">
          <button className="rs-btn-back" onClick={handleBack}>
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      ) : (
        <div className="rs-header-nav" style={{ paddingBottom: '16px' }}>
          <button className="rs-btn-back" onClick={handleBack} style={{ padding: 0 }}>
            <ChevronLeft size={20} />
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Interview Result</span>
          </button>
        </div>
      )}

      {/* ── COMPACT TOP SECTION (Aligned with Main Grid) ─────────────────────────────── */}
      <motion.div className="rs-main-grid" {...motionProps} style={{ marginBottom: '12px', alignItems: 'stretch' }}>

        {/* 1. MERGED — Candidate Card */}
        <div style={{
          background: '#0d6e5b',
          borderRadius: 20,
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(13,110,91,0.22)',
          position: 'relative',
          gap: 16,
        }}>
          {/* Dot pattern bg */}
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
            backgroundSize: '14px 14px', pointerEvents: 'none', zIndex: 0
          }} />

          {/* Left: name + session + round */}
          <div style={{ position: 'relative', zIndex: 1, flex: '1 1 200px' }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: '0 0 4px', lineHeight: 1.1 }}>{candidateName}</h1>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: '0 0 14px', fontWeight: 500, lineHeight: 1.4 }}>
              {feedback.session_name || 'Interview Session'}&nbsp;•&nbsp;{roundName}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {/* Verdict pill */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                background: verdictClass === 'success' ? 'rgba(34,197,94,0.18)' : verdictClass === 'fail' ? 'rgba(239,68,68,0.18)' : 'rgba(245,158,11,0.18)',
                border: `1px solid ${verdictClass === 'success' ? 'rgba(34,197,94,0.45)' : verdictClass === 'fail' ? 'rgba(239,68,68,0.45)' : 'rgba(245,158,11,0.45)'}`,
                borderRadius: 20, padding: '5px 14px',
              }}>
                <div style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: verdictClass === 'success' ? '#22c55e' : verdictClass === 'fail' ? '#ef4444' : '#f59e0b',
                  boxShadow: `0 0 8px ${verdictClass === 'success' ? '#22c55e' : verdictClass === 'fail' ? '#ef4444' : '#f59e0b'}`
                }} />
                <span style={{
                  fontSize: 12, fontWeight: 800, letterSpacing: '0.8px',
                  color: verdictClass === 'success' ? '#4ade80' : verdictClass === 'fail' ? '#fca5a5' : '#fcd34d'
                }}>{verdict.toUpperCase()}</span>
              </div>

              {/* Detailed Analysis Button */}
              <button 
                onClick={handleViewDetailed}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#ffffff', color: '#0d6e5b',
                  border: 'none',
                  borderRadius: 20, padding: '5px 16px',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  letterSpacing: '0.5px'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.transform = 'none'; }}
              >
                Detailed Analysis <ArrowRight size={14} />
              </button>
            </div>
          </div>

          {/* Right: Score highlight */}
          <div style={{
            position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            background: '#ffffff', borderRadius: 16, padding: '10px 18px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.6px', textTransform: 'uppercase', marginBottom: 2 }}>Score</span>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
              <span style={{ fontSize: 34, fontWeight: 900, color: '#0d6e5b', lineHeight: 1 }}><AnimatedScore value={Math.round(overallScore)} /></span>
              <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 600, marginBottom: 3 }}>/100</span>
            </div>
          </div>
        </div>

        {/* Right Column (nested grid): ATS Scan & Integrity */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '14px', alignItems: 'stretch' }}>
          
          {/* 3. ATS Scan */}
          {feedback.resume_score && (
          <div className="rs-card-box" style={{ justifyContent: 'space-between' }}>
            <div className="rs-card-box-header">
              <div className="rs-card-box-title">
                <FileScan size={18} /> ATS SCAN
              </div>
              <div className="rs-card-box-value" style={{ color: '#0f172a' }}>
                {typeof feedback.resume_score === 'object' ? feedback.resume_score.total : feedback.resume_score}%
              </div>
            </div>
            <hr className="rs-card-divider" />
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, justifyContent: 'flex-end' }}>
              {typeof feedback.resume_score === 'object' && [
                { label: 'Readability', val: feedback.resume_score.readability },
                { label: 'Impact',      val: feedback.resume_score.impact },
                { label: 'Conciseness', val: feedback.resume_score.conciseness },
                { label: 'Clarity',     val: feedback.resume_score.tech_clarity },
              ].map(({ label, val }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 12, color: '#475569', fontWeight: 600, width: 80 }}>{label}</span>
                  <div style={{ flex: 1, height: 6, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${(val / 10) * 100}%` }}
                      transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
                      style={{ 
                        height: '100%', 
                        background: val >= 7.5 ? '#0d6e5b' : val >= 5 ? '#f59e0b' : '#ef4444', 
                        borderRadius: 4 
                      }} 
                    />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', width: 24, textAlign: 'right' }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. Integrity */}
        {proctorData && (
          <div style={{
            background: '#fff', borderRadius: 20, border: '1px solid #f1f5f9',
            padding: '16px 20px', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
          }}>
            {(() => {
              const summary = proctorData.ai_report?.integrity_summary;
              const score = summary?.integrity_score ?? proctorData.integrity_score ?? 100;
              const status = summary?.overall_status ?? proctorData.status ?? 'VERIFIED';
              const isClean = status.toUpperCase() === 'CLEAN' || status.toUpperCase() === 'VERIFIED';
              const isCritical = status.toUpperCase() === 'CRITICAL' || status.toUpperCase() === 'FAILED';
              const message = summary?.risk_summary ?? (isClean ? 'No anomalies detected.' : 'Session flagged for review.');

              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                      <Shield size={14} /> INTEGRITY
                    </div>
                    <span style={{ fontSize: 18, fontWeight: 800, color: isCritical ? '#ef4444' : (isClean ? '#0d6e5b' : '#f59e0b') }}>{score}%</span>
                  </div>
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div className={`rs-integrity-icon-ring ${isClean ? '' : isCritical ? 'fail' : 'warning'}`}
                      style={{ width: 44, height: 44, fontSize: 0 }}>
                      {isClean ? <Check size={22} /> : <Shield size={22} />}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginTop: 4, textTransform: 'uppercase' }}>
                      {isClean ? 'SESSION CLEAN' : status}
                    </span>
                    <span style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', lineHeight: 1.4 }}>
                      {message}
                    </span>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        </div> {/* End of Right Column nested grid */}
      </motion.div>

      {/* Main Grid below */}
      <div className="rs-main-grid">


        {/* ROW 2 — Technical Scorecard (left) + Core Competencies (right) */}
        {filteredTechSkills.length > 0 && (
          <motion.div className="rs-card-box rs-col-left" style={{ alignSelf: 'start' }} {...motionProps} transition={{ delay: 0.2 }}>
            <div className="rs-card-box-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="rs-card-box-title">
                <Target size={18} /> TECHNICAL SCORECARD
              </div>
              <button 
                onClick={() => setIsTechModalOpen(true)}
                style={{
                  background: 'none', border: 'none', color: '#0d6e5b', fontSize: '12px',
                  fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0
                }}
              >
                Expand
              </button>
            </div>
            <hr className="rs-card-divider" />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <TechBarChart items={filteredTechSkills.map((s: any) => ({ name: s.skill, value: s.score, max: 10 }))} />
            </div>
          </motion.div>
        )}

        {/* Core Competencies */}
        {coreCompetenciesArray.length > 0 && (
          <motion.div className="rs-card-box rs-col-right" {...motionProps} transition={{ delay: 0.25 }}>
            <div className="rs-card-box-header">
              <div className="rs-card-box-title">
                <Layers size={18} /> CORE COMPETENCIES
              </div>
            </div>
            <hr className="rs-card-divider" />
            <RadarScoreChart
              data={radarData}
              overallScore={feedback?.core_competencies_report?.overall_competency_score}
              overallMax={10}
            />
          </motion.div>
        )}

        {/* CODING EVALUATION */}
        {isCodingFeedback && feedback.coding_evaluation && (
          <motion.div className="rs-card-box rs-col-right" {...motionProps} transition={{ delay: 0.25 }}>
            <div className="rs-card-box-header">
              <div className="rs-card-box-title">
                <Code size={18} /> CODING EVALUATION
              </div>
            </div>
            <hr className="rs-card-divider" />
            <div style={{ flex: 1 }}>
              <ScoreBarChart 
                items={[{
                  label: "Overall Coding Score",
                  value: feedback.coding_evaluation.overall_score || feedback.technical_readiness_score * 10,
                  max: 100
                }]} 
              />
            </div>
          </motion.div>
        )}

        {/* BEHAVIORAL EVALUATION */}
        {feedback.behavioral_evaluation && (
          <motion.div className="rs-card-box" style={{ gridColumn: '1 / -1' }} {...motionProps} transition={{ delay: 0.25 }}>
            <div className="rs-card-box-header">
              <div className="rs-card-box-title">
                <Brain size={18} /> BEHAVIORAL EVALUATION
              </div>
            </div>
            <hr className="rs-card-divider" />
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'stretch' }}>
              <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>SOFT SKILLS MATRIX</h3>
                <MatrixDonutGroup 
                  data={[
                    { name: "Initial Reaction", value: feedback.behavioral_evaluation.initial_reaction || 0, max: 5 },
                    { name: "Stakeholder Awareness", value: feedback.behavioral_evaluation.stakeholder_awareness || 0, max: 5 },
                    { name: "Resolution Logic", value: feedback.behavioral_evaluation.resolution_logic || 0, max: 5 }
                  ]} 
                />
              </div>
              <div style={{ flex: '2 1 400px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>JUSTIFICATION & TAGS</h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#64748b', lineHeight: 1.7 }}>
                  {feedback.behavioral_evaluation.justification || feedback.behavioral_evaluation.behavioral_narrative || feedback.behavioral_evaluation.narrative || "No behavioral justification provided."}
                </p>
                {feedback.behavioral_evaluation.behavioral_tags && feedback.behavioral_evaluation.behavioral_tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {feedback.behavioral_evaluation.behavioral_tags.map((tag: string, idx: number) => (
                      <span key={idx} style={{ background: '#f1f5f9', color: '#475569', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 600 }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* HR EVALUATION */}
        {feedback.hr_evaluation && (
          <motion.div className="rs-card-box" style={{ gridColumn: '1 / -1' }} {...motionProps} transition={{ delay: 0.3 }}>
            <div className="rs-card-box-header">
              <div className="rs-card-box-title">
                <Users size={18} /> HR EVALUATION
              </div>
            </div>
            <hr className="rs-card-divider" />
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'stretch' }}>
              <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>SOFT SKILLS MATRIX</h3>
                <MatrixDonutGroup 
                  data={[
                    { name: "Motivation", value: feedback.hr_evaluation.motivation_score || feedback.hr_evaluation.cultural_fit_score || 0, max: 10 },
                    { name: "Self Awareness", value: feedback.hr_evaluation.self_awareness_score || feedback.hr_evaluation.compensation_alignment_score || 0, max: 10 },
                    { name: "Operational Fit", value: feedback.hr_evaluation.operational_fit_score || feedback.hr_evaluation.role_understanding_score || 0, max: 10 }
                  ]} 
                />
              </div>
              <div style={{ flex: '2 1 400px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>QUALITATIVE NARRATIVE</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.55 }}>
                  {feedback.hr_evaluation.hr_narrative || feedback.hr_evaluation.narrative || "No HR narrative provided."}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* HM EVALUATION */}
        {feedback.hm_evaluation && (
          <motion.div className="rs-card-box" style={{ gridColumn: '1 / -1' }} {...motionProps} transition={{ delay: 0.35 }}>
            <div className="rs-card-box-header">
              <div className="rs-card-box-title">
                <Briefcase size={18} /> HM EVALUATION
              </div>
            </div>
            <hr className="rs-card-divider" />
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'stretch' }}>
              <div style={{ flex: '1 1 300px', minWidth: '300px' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>SOFT SKILLS MATRIX</h3>
                <MatrixDonutGroup 
                  data={[
                    { name: "Strategic Thinking", value: feedback.hm_evaluation.strategic_thinking_score || 0, max: 10 },
                    { name: "Leadership", value: feedback.hm_evaluation.leadership_potential_score || 0, max: 10 },
                    { name: "Team Synergy", value: feedback.hm_evaluation.team_synergy_score || 0, max: 10 }
                  ]} 
                />
              </div>
              <div style={{ flex: '2 1 400px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#000', textTransform: 'uppercase', letterSpacing: '0.5px' }}>QUALITATIVE NARRATIVE</h3>
                <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.55 }}>
                  {feedback.hm_evaluation.hm_narrative || feedback.hm_evaluation.narrative || "No hiring manager narrative provided."}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Tech Scorecard Modal */}
      <Modal open={isTechModalOpen} onClose={() => setIsTechModalOpen(false)} title="Technical Scorecard" width="500px">
        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '4px' }}>
          <TechBarChart 
            items={filteredTechSkills.map((s: any) => ({ name: s.skill, value: s.score, max: 10 }))} 
            maxItems={100} 
          />
        </div>
      </Modal>

    </div>
  );
};

export default ResultStatsPage;
